import { TagField } from "@/components/generic/TagField";
import { FieldsForm, SingleFieldMapping } from "@/types/fieldsFormSchema";
import { ProviderConnectionInfoField } from "@/types/integrations";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  Info,
  RefreshCw,
  RefreshCwOff,
  Trash,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Focusable, Button as RACButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button } from "../generic/Button";
import { Heading } from "../generic/Heading";
import { Tooltip } from "../generic/Tooltip";

export type FieldMappingsModel = FieldsForm["fieldMappings"][string];

export function FieldMappings({
  virkFieldsByEntity,
  integrationFieldsByVirkEntity,
  mappingGroups,
  mappingGroup,
  value,
  onChange,
  onDelete,
}: {
  virkFields: ProviderConnectionInfoField[];
  virkFieldsByEntity: Map<string, Map<string, ProviderConnectionInfoField>>;
  integrationFields: ProviderConnectionInfoField[];
  integrationFieldsByVirkEntity: Map<
    string,
    Map<string, ProviderConnectionInfoField>
  >;
  mappingGroups: { label: string; value: string }[];
  mappingGroup: string | null;
  value: FieldMappingsModel;
  onChange: (value: FieldMappingsModel) => void;
  onDelete: (entity: SingleFieldMapping) => void;
}) {
  const fieldsByCompositeKey = useMemo(() => {
    const fieldsByCompositeKey = new Map<
      string,
      {
        virkEntity: string;
        virkEntityName: string | null;
        integrationEntity: string;
        integrationEntityName: string | null;
        virkFields: ProviderConnectionInfoField[];
        integrationFields: ProviderConnectionInfoField[];
        fieldMappingModels: SingleFieldMapping[];
      }
    >();

    // Create entity groups for this mapping group
    for (const [
      virkEntity,
      integrationFields,
    ] of integrationFieldsByVirkEntity) {
      const virkFields =
        [...(virkFieldsByEntity.get(virkEntity)?.values() ?? [])]?.filter(
          (virkField) => virkField.visible,
        ) ?? [];

      if (!virkFields.length) {
        console.debug(
          "Skipping virkEntity with no visible fields:",
          virkEntity,
          virkFieldsByEntity.get(virkEntity),
        );
        continue;
      }

      for (const integrationField of integrationFields.values()) {
        if (
          !integrationField.visible ||
          (mappingGroups.length &&
            integrationField.mappingGroupId !== mappingGroup)
        ) {
          console.debug(
            "Skipping integrationField:",
            integrationField.fieldId,
            {
              visible: integrationField.visible,
              mappingGroupId: integrationField.mappingGroupId,
              selectedMappingGroupId: mappingGroup,
            },
          );
          continue;
        }

        const compositeKey = `${virkEntity}:${integrationField.entity}`;

        let entityFields = fieldsByCompositeKey.get(compositeKey);
        if (!entityFields) {
          entityFields = {
            virkEntity,
            virkEntityName: null,
            integrationEntity: integrationField.entity,
            integrationEntityName: null,
            virkFields,
            integrationFields: [],
            fieldMappingModels: [],
          };
          fieldsByCompositeKey.set(compositeKey, entityFields);

          for (const virkField of entityFields.virkFields) {
            if (virkField.entityName) {
              entityFields.virkEntityName = virkField.entityName;
              break;
            }
          }
        }

        entityFields.integrationFields.push(integrationField);
        entityFields.integrationEntityName ??= integrationField.entityName;
      }
    }

    if (value) {
      for (const [compositeKey, fieldMappingModels] of Object.entries(value)) {
        if (!fieldMappingModels) continue;

        const entityFields = fieldsByCompositeKey.get(compositeKey);
        if (entityFields) {
          entityFields.fieldMappingModels.push(...fieldMappingModels);
        }
      }
    }

    return fieldsByCompositeKey;
  }, [
    value,
    integrationFieldsByVirkEntity,
    mappingGroups.length,
    mappingGroup,
    virkFieldsByEntity,
  ]);

  useEffect(() => {
    console.debug("Fields by compositeKey:", fieldsByCompositeKey);
  }, [fieldsByCompositeKey]);

  const [optionalFieldMapping, setOptionalFieldMapping] = useState<
    Record<
      string,
      {
        to: ProviderConnectionInfoField | null;
        from: ProviderConnectionInfoField | null;
        update: boolean;
      }
    >
  >({});

  return (
    <div className="grid w-full grid-cols-[1fr_auto_1fr_auto] gap-x-5 gap-y-16">
      {[...fieldsByCompositeKey].map(([compositeKey, fields]) => (
        <div
          key={compositeKey}
          className="col-span-4 grid grid-cols-subgrid gap-y-4"
        >
          <Heading
            level={2}
            className="text-md col-span-4 grid grid-cols-subgrid font-medium"
          >
            <span className="text-center">{fields.virkEntityName}</span>
            <div />
            <span className="text-center">
              {fields.integrationEntityName || fields.integrationEntity}
            </span>
            <div />
          </Heading>
          {fields.fieldMappingModels.map((fieldValue, i) => (
            <EntityFieldMapping
              key={`${compositeKey}-mapping-${i}-${fieldValue.from?.fieldId}-${fieldValue.to?.fieldId}`}
              className={
                i === (fields.fieldMappingModels.length ?? 0) - 1 ? "mb-4" : ""
              }
              integrationFields={fields.integrationFields}
              virkFields={fields.virkFields}
              fromOptions={fields.virkFields.map((field) => ({
                label: field.fieldName || "",
                value: field.fieldId,
                updatable: field.updatable,
                tooltipText: field.description ?? undefined,
              }))}
              toOptions={fields.integrationFields
                .filter(
                  (iF) =>
                    iF.fieldId === fieldValue.to?.fieldId ||
                    !fields.fieldMappingModels.some(
                      (efmm) => efmm.to?.fieldId === iF.fieldId,
                    ),
                )
                .map((field) => ({
                  label: field.fieldName || "",
                  value: field.fieldId,
                  updatable: field.updatable,
                }))}
              ignoreRequired={
                // Allow deleting required fields if they are mapped multiple times
                !!fields.virkFields.find(
                  (f) =>
                    f.fieldId === fieldValue.from?.fieldId &&
                    f.required &&
                    fields.fieldMappingModels.filter(
                      (fm) => fm.from?.fieldId === f.fieldId,
                    ).length > 1,
                )
              }
              value={fieldValue}
              onDelete={() => onDelete(fieldValue)}
              onChange={(newFieldValue) => {
                if (!value?.[compositeKey]) return;

                const newFields = [...(value[compositeKey] ?? [])];

                if (!newFieldValue.to && fieldValue.to) {
                  // If to is being cleared, remove the mapping
                  newFields.splice(i, 1);
                } else {
                  newFields[i] = newFieldValue;
                }

                onChange({
                  ...value,
                  [compositeKey]: newFields,
                });
              }}
            />
          ))}
          <EntityFieldMapping
            intent="create"
            virkFields={fields.virkFields}
            integrationFields={fields.integrationFields}
            fromOptions={fields.virkFields.map((field) => ({
              label: field.fieldName || "",
              value: field.fieldId,
              updatable: field.updatable,
              tooltipText: field.description ?? undefined,
            }))}
            toOptions={fields.integrationFields
              .filter(
                (iF) =>
                  !fields.fieldMappingModels.some(
                    (efmm) => efmm.to?.fieldId === iF.fieldId,
                  ),
              )
              .map((field) => ({
                label: field.fieldName || "",
                value: field.fieldId,
                updatable: field.updatable,
                tooltipText: field.description ?? undefined,
              }))}
            ignoreRequired={true}
            value={
              optionalFieldMapping[compositeKey] ?? {
                from: null,
                to: null,
                update: false,
              }
            }
            onCreate={() => {
              if (
                optionalFieldMapping[compositeKey]?.from === null ||
                optionalFieldMapping[compositeKey]?.to === null
              )
                return;
              const newFields = [...(value?.[compositeKey] ?? [])];
              const newField = optionalFieldMapping[compositeKey];
              newFields.push(newField);
              onChange({
                ...(value ?? {}),
                [compositeKey]: newFields,
              });
              setOptionalFieldMapping((prev) => {
                const newState = { ...prev };
                delete newState[compositeKey];
                return newState;
              });
            }}
            onChange={(optionalFieldValue) => {
              setOptionalFieldMapping((prev) => {
                return {
                  ...prev,
                  [compositeKey]: {
                    from: optionalFieldValue.from,
                    to: optionalFieldValue.to,
                    update: optionalFieldValue.update,
                  },
                };
              });
            }}
          />
        </div>
      ))}
    </div>
  );
}

export function EntityFieldMapping({
  className,
  fromOptions,
  toOptions,
  value,
  onChange,
  onDelete,
  onCreate,
  integrationFields,
  virkFields,
  intent = "edit",
  ignoreRequired = false,
}: {
  className?: string;
  integrationFields: ProviderConnectionInfoField[];
  fromOptions: {
    label: string;
    value: string | number;
    updatable: boolean;
    tooltipText?: string;
  }[];
  toOptions: {
    label: string;
    value: string | number;
    updatable: boolean;
    tooltipText?: string;
  }[];
  value: SingleFieldMapping;
  onChange: (value: SingleFieldMapping) => void;
  onDelete?: () => void;
  onCreate?: () => void;
  virkFields: ProviderConnectionInfoField[];
  intent?: "edit" | "create";
  ignoreRequired?: boolean;
}) {
  const [from, setFrom] = useState(value.from?.fieldId);
  const [to, setTo] = useState(value.to?.fieldId);
  const [update, setUpdate] = useState(value.update);

  const modelValue = useMemo(
    () => ({
      from: virkFields.find((f) => f.fieldId === from) || null,
      to: integrationFields.find((f) => f.fieldId === to) || null,
      update,
    }),
    [from, to, update],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onChange(modelValue), [modelValue]);

  const toOptionsValue = useMemo(() => [...toOptions], [toOptions]);

  useEffect(() => {
    const foundFrom = fromOptions.find((option) => option.value === from);
    if (!foundFrom) return;
    if (!foundFrom.updatable && update) {
      setUpdate(false);
    }
  }, [from, fromOptions, update]);

  return (
    <div
      className={twMerge(
        "col-span-4 grid grid-cols-subgrid items-center",
        className,
      )}
    >
      <TagField
        className="w-auto"
        placeholder="Vælg fra-felt"
        trailingIcon={<ChevronDown className="size-3.5 xl:size-5" />}
        options={fromOptions}
        selectedOptions={
          fromOptions.find((option) => option.value === from) || []
        }
        description={
          !ignoreRequired &&
          (!!virkFields.find(
            (f) => f.fieldId === value.from?.fieldId && f.required,
          ) ||
            value.locked)
            ? "Dette felt er påkrævet"
            : undefined
        }
        isDisabled={
          !ignoreRequired &&
          (!!virkFields.find(
            (f) => f.fieldId === value.from?.fieldId && f.required,
          ) ||
            value.locked)
        }
        multiple={false}
        setSelectedOptions={(options) => {
          const option = Array.isArray(options) ? options[0] : options;
          setFrom(String(option.value));
        }}
      />
      <div className="size-5">
        <ArrowRight className="size-full" />
      </div>
      <TagField
        className="w-auto"
        placeholder="Vælg til-felt"
        trailingIcon={<ChevronDown className="size-3.5 xl:size-5" />}
        options={toOptionsValue}
        selectedOptions={
          toOptionsValue.find((option) => option.value === to) || []
        }
        multiple={false}
        isDisabled={!ignoreRequired && value.locked}
        setSelectedOptions={(options) => {
          const option = Array.isArray(options) ? options[0] : options;
          setTo(String(option.value));
        }}
      />
      <div className="flex items-center gap-x-6">
        <Tooltip
          tooltipText={
            update
              ? "Holdes opdateret ved ændringer"
              : "Opdateres ikke ved ændringer"
          }
          tooltipTriggerProps={{ delay: 500 }}
          placement="bottom"
        >
          <RACButton
            isDisabled={
              value.locked ||
              !(
                from &&
                to &&
                fromOptions.some((f) => f.value === from && f.updatable) &&
                toOptionsValue.some((f) => f.value === to && f.updatable)
              )
            }
            onPress={() => setUpdate((prev) => !prev)}
            className="hover:bg-theme-on-background-hover -m-2 cursor-pointer rounded-full p-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="sr-only">Opdater felt</span>
            {update ? (
              <RefreshCw
                className={twMerge(
                  "size-4",
                  update && "text-green-600 dark:text-green-400",
                )}
              />
            ) : (
              <RefreshCwOff className="text-theme-on-background-variant size-4" />
            )}
          </RACButton>
        </Tooltip>
        {intent === "edit" ? (
          !ignoreRequired &&
          (value.locked ||
            !!virkFields.find(
              (f) => f.fieldId === value.from?.fieldId && f.required,
            )) ? (
            <Tooltip tooltipText="Denne mapping er påkrævet og kan ikke slettes">
              <RACButton className="-m-2 p-2">
                <Info size={16} className="text-theme-on-background-variant" />
              </RACButton>
            </Tooltip>
          ) : (
            <RACButton
              onPress={() => {
                onDelete?.();
              }}
              className="hover:bg-theme-on-background-hover -m-2 cursor-pointer rounded-full p-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="sr-only">Slet mapping</span>
              <Trash className="text-theme-on-background-variant size-4" />
            </RACButton>
          )
        ) : null}
        {intent === "create" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ delay: 0.2 }}
          >
            <Button
              intent="primary"
              className="min-w-0"
              isDisabled={!from || !to}
              onPress={() => {
                setTo("");
                setFrom("");
                setUpdate(false);
                onCreate?.();
              }}
            >
              Tilføj
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
